import React, { useState } from "react";
import { authApi, markAuthed } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Compass, ArrowRight, EnvelopeSimple, ShieldCheck } from "@phosphor-icons/react";

const fmtErr = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return fb;
};

export const AuthScreen = ({ onAuthed }) => {
  const [mode, setMode] = useState("login"); // login | register
  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [devOtp, setDevOtp] = useState("");

  const sendCode = async () => {
    if (!email.trim()) { toast.error("Enter your business email."); return; }
    setBusy(true);
    try {
      const r = await authApi.requestOtp(email.trim());
      setStep("otp");
      if (r.dev_otp) { setDevOtp(r.dev_otp); toast.info(`DEV code: ${r.dev_otp}`); }
      else toast.success(r.message || "Code sent.");
    } catch (e) { toast.error(fmtErr(e, "Could not send code.")); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code.trim()) { toast.error("Enter the code."); return; }
    setBusy(true);
    try {
      const r = await authApi.verifyOtp(email.trim(), code.trim());
      markAuthed(true);
      toast.success("Signed in.");
      onAuthed(r.user);
    } catch (e) { toast.error(fmtErr(e, "Invalid code.")); }
    finally { setBusy(false); }
  };

  const register = async () => {
    if (!email.trim()) { toast.error("Enter your business email."); return; }
    setBusy(true);
    try {
      const r = await authApi.register(email.trim(), name.trim());
      if (r.status === "approved") { toast.success("Already registered — please sign in."); setMode("login"); }
      else {
        toast.success(r.message || "Registration received.");
        if (r.dev_approval_link) toast.info("DEV: admin approval link logged in backend.");
        setMode("login");
      }
    } catch (e) { toast.error(fmtErr(e, "Registration failed.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-4" data-testid="auth-screen">
      <div className="w-full max-w-md border border-[var(--border)] rounded-[6px] bg-[var(--surface)] overflow-hidden">
        <div className="px-6 py-5 border-b border-[var(--border)] bg-[var(--surface-2)] flex items-center gap-2">
          <Compass size={22} weight="bold" className="text-[var(--primary)]" />
          <div>
            <div className="font-mono text-lg text-[var(--text)]">LispForge</div>
            <div className="text-[11px] font-mono text-[var(--text-dim)] uppercase tracking-widest">AI CAD Automation</div>
          </div>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div className="flex gap-1 p-1 bg-[var(--bg)] rounded-[4px] border border-[var(--border)]">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setStep("email"); setCode(""); setDevOtp(""); }}
                className={`flex-1 h-8 rounded-[3px] text-xs font-mono uppercase tracking-wider transition-colors ${mode === m ? "bg-[var(--primary)] text-white" : "text-[var(--text-dim)] hover:text-[var(--text)]"}`}>
                {m === "login" ? "Sign in" : "Request access"}
              </button>
            ))}
          </div>

          {mode === "register" ? (
            <>
              <p className="text-[12px] text-[var(--text-dim)] leading-snug">
                Register with your <b>business email</b>. An administrator will review and approve your account before you can sign in.
              </p>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name"
                placeholder="Your name" className="h-10 rounded-[4px] bg-[var(--bg)] border-[var(--border)] font-mono text-sm" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-register-email"
                placeholder="you@yourfirm.com" className="h-10 rounded-[4px] bg-[var(--bg)] border-[var(--border)] font-mono text-sm" />
              <Button onClick={register} disabled={busy} data-testid="auth-register-submit"
                className="h-10 rounded-[4px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                <EnvelopeSimple size={16} weight="bold" /> {busy ? "Submitting…" : "Request access"}
              </Button>
            </>
          ) : step === "email" ? (
            <>
              <p className="text-[12px] text-[var(--text-dim)] leading-snug">Enter your approved business email — we'll send a one-time sign-in code.</p>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-login-email"
                onKeyDown={(e) => e.key === "Enter" && sendCode()}
                placeholder="you@yourfirm.com" className="h-10 rounded-[4px] bg-[var(--bg)] border-[var(--border)] font-mono text-sm" />
              <Button onClick={sendCode} disabled={busy} data-testid="auth-sendcode"
                className="h-10 rounded-[4px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                <ArrowRight size={16} weight="bold" /> {busy ? "Sending…" : "Send code"}
              </Button>
            </>
          ) : (
            <>
              <p className="text-[12px] text-[var(--text-dim)] leading-snug">
                Enter the 6-digit code sent to <b className="text-[var(--text)]">{email}</b>.
              </p>
              {devOtp && <div className="text-[11px] font-mono text-amber-500 bg-amber-500/10 rounded-[3px] px-2 py-1" data-testid="auth-dev-otp">DEV code (email not configured): {devOtp}</div>}
              <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="auth-otp"
                onKeyDown={(e) => e.key === "Enter" && verify()} maxLength={6}
                placeholder="123456" className="h-11 rounded-[4px] bg-[var(--bg)] border-[var(--border)] font-mono text-lg tracking-[8px] text-center" />
              <Button onClick={verify} disabled={busy} data-testid="auth-verify"
                className="h-10 rounded-[4px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                <ShieldCheck size={16} weight="bold" /> {busy ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button onClick={() => { setStep("email"); setCode(""); setDevOtp(""); }} data-testid="auth-back"
                className="text-[11px] font-mono text-[var(--text-dim)] hover:text-[var(--primary)]">← Use a different email</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
